import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { MapIcon, FlagIcon, MapPinIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Postcard, CountryData } from '../types';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in React-Leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom marker icon
const createCustomMarker = (country: CountryData, postcardCount: number) => {
  const hasPostcards = postcardCount > 0;
  
  return new L.DivIcon({
    html: `
      <div class="flex flex-col items-center">
        <div class="relative">
          <div class="w-10 h-10 bg-white rounded-full shadow-lg border-2 ${
            hasPostcards ? 'border-green-500' : 'border-gray-300'
          } flex items-center justify-center text-lg hover:scale-110 transition-transform duration-200">
            ${country.flag}
          </div>
          ${postcardCount > 0 ? `
            <div class="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold shadow-md">
              ${postcardCount > 9 ? '9+' : postcardCount}
            </div>
          ` : ''}
        </div>
      </div>
    `,
    className: 'custom-marker',
    iconSize: [40, 40],
    iconAnchor: [20, 40],
    popupAnchor: [0, -40]
  });
};

export function MapPage() {
  const { currentUser } = useAuth();
  const [postcards, setPostcards] = useState<Postcard[]>([]);
  const [countries, setCountries] = useState<CountryData[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const handleViewPostcards = (countryCode: string) => {
    setSelectedCountry(countryCode);
    // Smooth scroll to the postcards section after a brief delay to let the section render
    setTimeout(() => {
      const postcardsSection = document.querySelector(`[data-country="${countryCode}"]`);
      if (postcardsSection) {
        postcardsSection.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start',
          inline: 'nearest' 
        });
      }
    }, 100);
  };

  useEffect(() => {
    if (currentUser) {
      fetchData();
    }
    
    // Global event listener for postcard buttons in popups
    const handlePopupButtonClick = (event: Event) => {
      const target = event.target as HTMLElement;
      if (target.classList.contains('popup-view-postcards-btn')) {
        const countryCode = target.getAttribute('data-country-code');
        if (countryCode) {
          console.log('Popup button clicked for country:', countryCode);
          handleViewPostcards(countryCode);
        }
      }
    };

    document.addEventListener('click', handlePopupButtonClick);
    
    return () => {
      document.removeEventListener('click', handlePopupButtonClick);
    };
  }, [currentUser]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // First fetch postcards
      await fetchPostcards();
      // Note: fetchCountries will be called via useEffect when postcards state updates
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Process countries whenever postcards change
  useEffect(() => {
    if (postcards.length > 0) {
      processCountries();
    }
  }, [postcards]);

  const processCountries = () => {
    try {
      // Get all unique countries from postcards
      const uniqueCountries = new Set<string>();
      postcards.forEach(postcard => {
        if (postcard.country && postcard.country.trim() !== '') {
          uniqueCountries.add(postcard.country.toLowerCase());
        }
      });

      console.log('Unique countries found in postcards:', Array.from(uniqueCountries));

      // Define a comprehensive list of countries with their data
      const allCountries: { [key: string]: CountryData } = {
        'united states': {
          code: 'US',
          name: 'United States',
          flag: '🇺🇸',
          coordinates: { lat: 39.8283, lng: -98.5795 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'usa': {
          code: 'US',
          name: 'United States',
          flag: '🇺🇸',
          coordinates: { lat: 39.8283, lng: -98.5795 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'us': {
          code: 'US',
          name: 'United States',
          flag: '🇺🇸',
          coordinates: { lat: 39.8283, lng: -98.5795 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'france': {
          code: 'FR',
          name: 'France',
          flag: '🇫🇷',
          coordinates: { lat: 46.2276, lng: 2.2137 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'japan': {
          code: 'JP',
          name: 'Japan',
          flag: '🇯🇵',
          coordinates: { lat: 36.2048, lng: 138.2529 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'canada': {
          code: 'CA',
          name: 'Canada',
          flag: '🇨🇦',
          coordinates: { lat: 56.1304, lng: -106.3468 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'australia': {
          code: 'AU',
          name: 'Australia',
          flag: '🇦🇺',
          coordinates: { lat: -25.2744, lng: 133.7751 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'united kingdom': {
          code: 'GB',
          name: 'United Kingdom',
          flag: '🇬🇧',
          coordinates: { lat: 55.3781, lng: -3.4360 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'germany': {
          code: 'DE',
          name: 'Germany',
          flag: '🇩🇪',
          coordinates: { lat: 51.1657, lng: 10.4515 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'italy': {
          code: 'IT',
          name: 'Italy',
          flag: '🇮🇹',
          coordinates: { lat: 41.8719, lng: 12.5674 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'spain': {
          code: 'ES',
          name: 'Spain',
          flag: '🇪🇸',
          coordinates: { lat: 40.4637, lng: -3.7492 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'india': {
          code: 'IN',
          name: 'India',
          flag: '🇮🇳',
          coordinates: { lat: 20.5937, lng: 78.9629 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'china': {
          code: 'CN',
          name: 'China',
          flag: '🇨🇳',
          coordinates: { lat: 35.8617, lng: 104.1954 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'brazil': {
          code: 'BR',
          name: 'Brazil',
          flag: '🇧🇷',
          coordinates: { lat: -14.2350, lng: -51.9253 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        },
        'mexico': {
          code: 'MX',
          name: 'Mexico',
          flag: '🇲🇽',
          coordinates: { lat: 23.6345, lng: -102.5528 },
          postcardCount: 0,
          visited: false,
          sentCount: 0,
          receivedCount: 0
        }
      };

      // Create a map to track unique countries by their normalized name
      const countryMap = new Map<string, CountryData>();

      // Process each unique country from postcards
      uniqueCountries.forEach(countryName => {
        const normalizedName = countryName.toLowerCase();
        const countryData = allCountries[normalizedName];
        
        if (countryData) {
          // Calculate postcard counts for this country
          const countryPostcards = getPostcardsByCountry(countryData.name);
          
          const sentCount = countryPostcards.filter(postcard => 
            postcard.createdBy === currentUser?.uid
          ).length;
          
          const receivedCount = countryPostcards.filter(postcard => 
            postcard.createdBy !== currentUser?.uid
          ).length;
          
          countryData.postcardCount = countryPostcards.length;
          countryData.visited = countryPostcards.length > 0;
          countryData.sentCount = sentCount;
          countryData.receivedCount = receivedCount;
          
          // Use country code as key to avoid duplicates
          countryMap.set(countryData.code, countryData);
        } else {
          // For unknown countries, create a generic entry
          console.log(`Unknown country: ${countryName}, creating generic entry`);
          const genericCountry: CountryData = {
            code: countryName.toUpperCase().substring(0, 2),
            name: countryName.charAt(0).toUpperCase() + countryName.slice(1),
            flag: '🌍',
            coordinates: { lat: 0, lng: 0 }, // Will be updated if we find coordinates
            postcardCount: 0,
            visited: false,
            sentCount: 0,
            receivedCount: 0
          };
          
          const countryPostcards = getPostcardsByCountry(genericCountry.name);
          genericCountry.postcardCount = countryPostcards.length;
          genericCountry.visited = countryPostcards.length > 0;
          genericCountry.sentCount = countryPostcards.filter(postcard => 
            postcard.createdBy === currentUser?.uid
          ).length;
          genericCountry.receivedCount = countryPostcards.filter(postcard => 
            postcard.createdBy !== currentUser?.uid
          ).length;
          
          countryMap.set(genericCountry.code, genericCountry);
        }
      });

      // Convert map to array
      const countriesData = Array.from(countryMap.values());
      
      console.log('Final countries data:', countriesData);
      setCountries(countriesData);
    } catch (error) {
      console.error('Error processing countries:', error);
    }
  };

  const fetchPostcards = async () => {
    if (!currentUser) return;

    try {
      // Fetch all postcards (public ones and user's own postcards)
      const publicQuery = query(
        collection(db, 'postcards'),
        where('isPublic', '==', true)
      );
      
      const userQuery = query(
        collection(db, 'postcards'),
        where('createdBy', '==', currentUser.uid)
      );
      
      const [publicSnapshot, userSnapshot] = await Promise.all([
        getDocs(publicQuery),
        getDocs(userQuery)
      ]);
      
      const postcardsData: Postcard[] = [];
      
      // Add public postcards
      publicSnapshot.forEach((doc) => {
        const data = doc.data();
        postcardsData.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date()
        } as Postcard);
      });
      
      // Add user's own postcards (including private ones)
      userSnapshot.forEach((doc) => {
        const data = doc.data();
        // Only add if not already in the list (avoid duplicates)
        if (!postcardsData.find(p => p.id === doc.id)) {
          postcardsData.push({
            id: doc.id,
            ...data,
            createdAt: data.createdAt?.toDate() || new Date()
          } as Postcard);
        }
      });
      
      console.log('Fetched postcards:', postcardsData.length, postcardsData);
      console.log('Postcard countries:', postcardsData.map(p => ({ id: p.id, country: p.country, location: p.location })));
      setPostcards(postcardsData);
    } catch (error) {
      console.error('Error fetching postcards:', error);
    }
  };

  const getPostcardsByCountry = (countryName: string) => {
    if (!countryName || !postcards.length) return [];
    
    // Enhanced country matching with more variations
    const countryVariations = [
      countryName.toLowerCase(),
      countryName.toLowerCase().replace(/\s+/g, ''),
      countryName.toLowerCase().replace(/\s+/g, '-'),
      countryName.toLowerCase().replace(/\s+/g, '_'),
      // Add common abbreviations
      countryName === 'United States' ? 'usa' : '',
      countryName === 'United States' ? 'us' : '',
      countryName === 'United States' ? 'united states of america' : '',
      countryName === 'France' ? 'france' : '',
      countryName === 'Japan' ? 'japan' : '',
      // Add partial matches
      countryName.toLowerCase().split(' ')[0], // First word
      countryName.toLowerCase().split(' ').slice(-1)[0], // Last word
    ].filter(Boolean); // Remove empty strings
    
    console.log(`Looking for country: "${countryName}" with variations:`, countryVariations);
    
    const matchedPostcards = postcards.filter(p => {
      if (!p.country && !p.location) {
        console.log(`Postcard ${p.id} has no country or location`);
        return false;
      }
      
      const postcardCountry = (p.country || '').toLowerCase();
      const postcardLocation = (p.location || '').toLowerCase();
      
      console.log(`Checking postcard ${p.id}: country="${postcardCountry}", location="${postcardLocation}"`);
      
      // Check if any variation matches
      const matches = countryVariations.some(variation => {
        const countryMatch = postcardCountry.includes(variation) || variation.includes(postcardCountry);
        const locationMatch = postcardLocation.includes(variation) || variation.includes(postcardLocation);
        return countryMatch || locationMatch;
      });
      
      if (matches) {
        console.log(`✓ Postcard ${p.id} matches ${countryName}`);
      }
      
      return matches;
    });
    
    console.log(`Found ${matchedPostcards.length} postcards for ${countryName}`);
    return matchedPostcards;
  };

  const visitedCountries = countries.filter(c => c.visited);
  const totalPostcards = postcards.length;

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-accent-600 to-primary-600 rounded-xl p-6 text-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-3xl font-bold mb-2">World Map</h1>
          <p className="text-accent-100">
            Explore your global postcard journey
          </p>
        </motion.div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card text-center">
          <div className="text-2xl font-bold text-primary-600">{totalPostcards}</div>
          <div className="text-gray-600">Total Postcards</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-secondary-600">{visitedCountries.length}</div>
          <div className="text-gray-600">Countries Visited</div>
        </div>
        <div className="card text-center">
          <div className="text-2xl font-bold text-accent-600">{countries.length}</div>
          <div className="text-gray-600">Total Countries</div>
        </div>
      </div>

      {/* Interactive Map */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <MapIcon className="h-5 w-5 mr-2 text-accent-600" />
          Interactive World Map
        </h2>
        
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-600"></div>
          </div>
        ) : (
          <div className="h-96 rounded-lg overflow-hidden">
            <MapContainer
              center={[20, 0]}
              zoom={2}
              style={{ height: '100%', width: '100%' }}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              />
              
              {countries.map((country) => {
                const countryPostcards = getPostcardsByCountry(country.name);
                return (
                  <Marker
                    key={country.code}
                    position={[country.coordinates.lat, country.coordinates.lng]}
                    icon={createCustomMarker(country, countryPostcards.length)}
                    eventHandlers={{
                      click: () => handleViewPostcards(country.code)
                    }}
                  >
                    <Popup
                      closeButton={true}
                      className="custom-popup"
                      maxWidth={280}
                      minWidth={200}
                    >
                      <div className="p-2">
                        <div className="text-center space-y-3">
                          <div className="text-3xl">{country.flag}</div>
                          <div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">{country.name}</h3>
                            <p className="text-sm text-gray-600 mb-3">
                              {countryPostcards.length} postcard{countryPostcards.length !== 1 ? 's' : ''}
                              {countryPostcards.length > 0 ? ' shared' : ' yet'}
                            </p>
                          </div>
                          <div dangerouslySetInnerHTML={{
                            __html: countryPostcards.length > 0 ? 
                              `<button 
                                class="popup-view-postcards-btn w-full px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-md hover:shadow-lg cursor-pointer"
                                data-country-code="${country.code}"
                              >
                                View Postcards
                              </button>` :
                              '<div class="text-xs text-gray-500 italic">No postcards from this country yet</div>'
                          }} />
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        )}
      </div>

      {/* Countries List */}
      <div className="card">
        <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
          <FlagIcon className="h-5 w-5 mr-2 text-primary-600" />
          Countries Overview
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {countries.map((country) => {
            const countryPostcards = getPostcardsByCountry(country.name);
            return (
              <motion.div
                key={country.code}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                  country.visited 
                    ? 'border-green-500 bg-green-50' 
                    : 'border-gray-200 bg-gray-50'
                } ${selectedCountry === country.code ? 'ring-2 ring-primary-500' : ''}`}
                onClick={() => setSelectedCountry(country.code)}
              >
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center">
                    <span className="text-2xl mr-2">{country.flag}</span>
                    <span className="font-medium text-gray-900">{country.name}</span>
                  </div>
                  {country.visited && (
                    <span className="text-green-600 text-sm">✓ Visited</span>
                  )}
                </div>
                <div className="text-sm text-gray-600">
                  {countryPostcards.length} postcard{countryPostcards.length !== 1 ? 's' : ''}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Selected Country Postcards */}
      {selectedCountry && (
        <div className="card" data-country={selectedCountry}>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900 flex items-center">
              <MapPinIcon className="h-5 w-5 mr-2 text-primary-600" />
              {countries.find(c => c.code === selectedCountry)?.name} Postcards
            </h2>
            <button
              onClick={() => setSelectedCountry(null)}
              className="text-gray-500 hover:text-gray-700"
            >
              ✕
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {getPostcardsByCountry(countries.find(c => c.code === selectedCountry)?.name || '').map((postcard) => (
              <div key={postcard.id} className="border rounded-lg overflow-hidden">
                <img
                  src={postcard.imageURL}
                  alt="Postcard"
                  className="w-full h-32 object-cover"
                />
                <div className="p-3">
                  <p className="text-sm text-gray-900 font-medium line-clamp-2 mb-1">
                    {postcard.message}
                  </p>
                  <p className="text-xs text-gray-600">
                    {postcard.location}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
} 